/**
 * Invitations API – lightweight counts for tab badge and Friends/Create UI.
 * GET /invitations/counts returns { friendReceived, groupReceived } without fetching full lists.
 */

import type { AxiosResponse } from 'axios';
import { API_ENDPOINTS } from '../../../constants';
import { extractErrorMessage, isNetworkError } from '../../../helpers/apiClient.helper';
import { logger } from '../../helpers/logger.helper';

export interface InvitationCounts {
  friendReceived: number;
  groupReceived: number;
}

export interface InvitationCountsResponse {
  success: boolean;
  data?: InvitationCounts;
}

/**
 * Fetch invitation counts (friend + group received pending) for the current user.
 * Requires authenticated API client (Supabase JWT token).
 */
export async function fetchInvitationCounts(
  apiClientFn: <T = unknown>(endpoint: string, config?: any) => Promise<AxiosResponse<T>>
): Promise<InvitationCounts> {
  try {
    const endpoint = API_ENDPOINTS.invitations.counts;
    const { data } = await apiClientFn<InvitationCountsResponse>(endpoint);

    if (data?.success && data.data) {
      return {
        friendReceived: typeof data.data.friendReceived === 'number' ? data.data.friendReceived : 0,
        groupReceived: typeof data.data.groupReceived === 'number' ? data.data.groupReceived : 0,
      };
    }
    return { friendReceived: 0, groupReceived: 0 };
  } catch (error) {
    const msg = extractErrorMessage(error, 'Failed to fetch invitation counts');
    if (isNetworkError(error)) {
      logger.warn('fetchInvitationCounts failed: server unreachable');
    } else {
      logger.error('fetchInvitationCounts failed', { message: msg });
    }
    return { friendReceived: 0, groupReceived: 0 };
  }
}
